// Audio player with playback-speed cycling (1x → 1.25x → 1.5x → 2x) and an
// imperative seek API so the transcript can click-timestamp-to-seek. Reports
// playback position upward for sync-highlighting the current transcript turn.

import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

export interface HxAudioHandle {
  seek(sec: number): void;
}

const SPEEDS = [1, 1.25, 1.5, 2];

function fmt(sec: number): string {
  if (!Number.isFinite(sec)) return "0:00";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

export const HxAudioPlayer = forwardRef<HxAudioHandle, {
  src: string;
  onTimeUpdate?: (sec: number) => void;
}>(function HxAudioPlayer({ src, onTimeUpdate }, ref) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const railRef = useRef<HTMLDivElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speedIdx, setSpeedIdx] = useState(0);
  const [failed, setFailed] = useState(false);

  useImperativeHandle(ref, () => ({
    seek(sec: number) {
      const a = audioRef.current;
      if (!a) return;
      a.currentTime = sec;
      void a.play().catch(() => {});
    },
  }));

  useEffect(() => {
    const a = audioRef.current;
    if (a) a.playbackRate = SPEEDS[speedIdx];
  }, [speedIdx]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) void a.play().catch(() => setFailed(true));
    else a.pause();
  };

  const onRailClick = (e: React.MouseEvent) => {
    const a = audioRef.current;
    const rail = railRef.current;
    if (!a || !rail || !duration) return;
    const rect = rail.getBoundingClientRect();
    a.currentTime = ((e.clientX - rect.left) / rect.width) * duration;
  };

  if (failed) {
    return <div className="hx-alert">Recording could not be loaded. <a href={src} target="_blank" rel="noreferrer">Open directly</a></div>;
  }

  return (
    <div className="hx-audio">
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onError={() => setFailed(true)}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
        onTimeUpdate={(e) => {
          const t = e.currentTarget.currentTime;
          setTime(t);
          onTimeUpdate?.(t);
        }}
      />
      {/* SVG glyphs, not text: "▶" sits off-centre in the circle because of
          its font baseline. The play triangle is nudged 1px right so it reads
          as optically centred. */}
      <button type="button" className="play" onClick={toggle} aria-label={playing ? "Pause" : "Play"}>
        {playing ? (
          <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true"><rect x="2" y="1.5" width="3" height="9" rx="0.8" /><rect x="7" y="1.5" width="3" height="9" rx="0.8" /></svg>
        ) : (
          <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true" style={{ marginLeft: 1 }}><path d="M3 1.6v8.8c0 .6.65.95 1.15.62l6.4-4.4a.75.75 0 0 0 0-1.24l-6.4-4.4A.75.75 0 0 0 3 1.6z" /></svg>
        )}
      </button>
      <span className="t tnum">{fmt(time)}</span>
      <div className="rail" ref={railRef} onClick={onRailClick}>
        <i style={{ width: duration ? `${(time / duration) * 100}%` : "0%" }} />
      </div>
      <span className="t tnum">{fmt(duration)}</span>
      <button
        type="button"
        className="speed"
        onClick={() => setSpeedIdx((i) => (i + 1) % SPEEDS.length)}
        title="Playback speed"
      >
        {SPEEDS[speedIdx]}x
      </button>
    </div>
  );
});
